import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';

interface TooltipActions {
    showTooltip: (text: string, event?: React.MouseEvent) => void;
    hideTooltip: () => void;
}

interface TooltipState {
    tooltipText: string | null;
    position: { x: number; y: number };
}

const TooltipActionsContext = createContext<TooltipActions | undefined>(undefined);
const TooltipStateContext = createContext<TooltipState | undefined>(undefined);

export const TooltipProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [tooltipText, setTooltipText] = useState<string | null>(null);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isTouchDevice, setIsTouchDevice] = useState(false);
    const lastMousePos = React.useRef({ x: 0, y: 0 });

    useEffect(() => {
        const handleTouch = () => {
            setIsTouchDevice(true);
            setTooltipText(null);
        };

        const handlePointerDown = (event: PointerEvent) => {
            setTooltipText(null);
            if (event.pointerType === 'touch') {
                handleTouch();
            }
        };

        window.addEventListener('touchstart', handleTouch, { passive: true, once: true });
        window.addEventListener('pointerdown', handlePointerDown, { passive: true });
        return () => {
            window.removeEventListener('touchstart', handleTouch);
            window.removeEventListener('pointerdown', handlePointerDown);
        };
    }, []);

    useEffect(() => {
        const handleMouseMove = (event: MouseEvent) => {
            lastMousePos.current = { x: event.clientX, y: event.clientY };
            if (tooltipText) {
                setPosition({ x: event.clientX, y: event.clientY });
            }
        };

        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, [tooltipText]);

    const showTooltip = useCallback((text: string, event?: React.MouseEvent) => {
        if (isTouchDevice) {
            return;
        }
        setTooltipText(text);
        if (event) {
            setPosition({ x: event.clientX, y: event.clientY });
        } else {
            setPosition(lastMousePos.current);
        }
    }, [isTouchDevice]);

    const hideTooltip = useCallback(() => {
        setTooltipText(null);
    }, []);

    const actions = useMemo(() => ({ showTooltip, hideTooltip }), [showTooltip, hideTooltip]);
    const state = useMemo(() => ({ tooltipText, position }), [tooltipText, position]);

    return (
        <TooltipActionsContext.Provider value={actions}>
            <TooltipStateContext.Provider value={state}>
                {children}
            </TooltipStateContext.Provider>
        </TooltipActionsContext.Provider>
    );
};

export const useTooltip = () => {
    const context = useContext(TooltipActionsContext);
    if (!context) {
        throw new Error('useTooltip must be used within a TooltipProvider');
    }
    return context;
};

export const useTooltipState = () => {
    const context = useContext(TooltipStateContext);
    if (!context) {
        throw new Error('useTooltipState must be used within a TooltipProvider');
    }
    return context;
};
